//
import { ApolloClient } from 'apollo-client'
import { InMemoryCache } from 'apollo-cache-inmemory'
import { HttpLink } from 'apollo-link-http'
import { onError } from 'apollo-link-error'
import getModel from './graphql/query/model.graphql'
import getLocalization from './graphql/query/getLocalization.graphql'
import getFullLocalization from './graphql/query/getFullLocalization.graphql'
import getComplexLocalization from './graphql/query/getComplexLocalization.graphql'
import appVersion from './graphql/query/appVersion.graphql'
import addAnalytics from './graphql/mutations/analytics/addAnalytics.graphql'
import gql from 'graphql-tag'
import auth from '@it-enterprise/jwtauthentication/dist/jwtauthentication.umd.min.js'
import Vue from 'vue'
import { processingApi } from '@/api/processingApi'
import { notificationApi } from '@/api/notificationApi'
import { i18n } from '../plugins/i18n'
import { deepCopy, getApplicationName } from '@/tools'
import store from '../store'

// show erros to user by notifications
let silentErrors = false

const errorLink = onError(({ graphQLErrors, networkError, response }) => {
  if (store.state.general.config.useOneSession && networkError && networkError.statusCode === 400 && networkError.result === false) {
    auth.logout({
      returnUrl: window.location.pathname,
      skipSigninSilent: store.getters.appModel?.ProjectSecurity?.OidcEnabled,
      lang: i18n.locale
    }).then(() => {
      store.commit('SET_USER', null)
      Vue.$routerCore.GoToSignIn()
    })
  }
  if (networkError && networkError.statusCode === 401) {
    auth.clearTokens()
    Vue.$routerCore.GoToSignIn()
  }
  processingApi.hideAll()
  if (graphQLErrors) {
    if (silentErrors) {
      return
    }
    // TODO: Обработать ошибку
    if (Array.isArray(graphQLErrors) && graphQLErrors.some(x => x.message.includes('NOT_ALLOWED_SCHEMA'))) {
      let errMsg = graphQLErrors.find(x => x.message.includes('NOT_ALLOWED_SCHEMA')).message.substr(18)
      notificationApi.showError({ 
        text: errMsg,
        showDetails: false
      })
      return errMsg
    }

    let unhandledError = graphQLErrors?.[0]?.message ?? ''
    let message = unhandledError.split('#exception#')?.[1]

    notificationApi.showError({ 
      text: message ?? i18n.t('messages.dataAccessError'), 
      dateTime: response?.dateTime, 
      requestId: response?.requestId 
    })
    return graphQLErrors.message
  }
})

const getClient = async (schema, isService = false) => {
  const token = isService ? await auth.getTokenForServiceRequest() : await auth.getToken()
  const options = {
    // eslint-disable-next-line no-undef
    uri: store.state.general.config.GraphQlUrl + 'api/graphql',
    credentials: 'include',
    headers: {
      Authorization: `Bearer ${token}`,
      // TODO: Убрать HRPORTAL
      schema: schema ?? 'HRPORTAL',
      'Accept-Language': i18n.locale, 
      'Application-Name': getApplicationName(),
      delegateUserId: window.sessionStorage.getItem('delegateUserId') || ''
    }
  }
  const httpLink = new HttpLink(options)
  
  return new ApolloClient({
    cache: new InMemoryCache({
      addTypename: false
    }),
    link: errorLink.concat(httpLink)
  })
}

export { getClient }

function getOperationsFromModel() {
  if (!Vue.$authCore.store.getters.DataSources) {
    return []
  }
  return Vue.$authCore.store.getters.DataSources.map(x => {
    return { $ID: x.$ID, Query: x.Query, Mutations: x.Mutations, GraphQlSchema: x.GraphQlSchema }
  })
}

async function callModelOperation(options, args) {
  let ds = getOperationsFromModel().find(x => x.$ID == options.dataSourceId)
  const client = await getClient(ds.GraphQlSchema)
  if (!options.operationId) {
    return await client.query({
      query: gql`${ds.Query}`,
      variables: args
    })
  }

  let mutation = ds.Mutations.find(x => x.$ID == options.operationId).Value
  return await client.mutate({
    mutation: gql`${mutation}`,
    variables: args
  })
}

export class GraphqQlApi {
  static async addAnalytics(data) {
    try {
      silentErrors = true
      const client = await getClient('WebAppModeler', true)
      return await client.mutate({
        mutation: gql`${addAnalytics}`,
        variables: { analytics: data }
      })
    } catch (e) {
      throw new Error(e.message)
    } finally {
      silentErrors = false
    }
  }

  static async callOperation(options, args) {
    try {
      return await callModelOperation(options, deepCopy(args))
    } catch (e) {
      throw new Error(e.message)
    }
  }

  // get model 
  static async getModelFromGqlAsync(appName, isSystem, skipCompressModel, useModelCache) {
    try {
      const client = await getClient('WebAppModeler')
      return await client.query({
        query: gql`${getModel}`,
        variables: { appName, isSystem, skipCompressModel, useModelCache }
      })
    } catch (e) {
      throw new Error(e.message)
    }
  }

  /**
   * Get system localization
   * @param {*} params 
   * @returns 
   */
  static async getLocalizationAsync(params) {
    try {
      const client = await getClient('WebAppModeler')
      return await client.query({
        query: gql`${getLocalization}`,
        variables: params
      })
    } catch (e) {
      throw new Error(e.message)
    }
  }

  /**
   * Get system and users localization
   * @param {*} params 
   * @returns 
   */
  static async getComplexLocalizationAsync(params) {
    let response = null

    try {
      silentErrors = true
      const client = await getClient('WebAppModeler')
      response = await client.query({
        query: gql`${getComplexLocalization}`,
        variables: params
      })
    } catch (e) {
      throw new Error(e.message)
    } finally {
      silentErrors = false
    }

    return response
  }

  /**
   * Get system and users localization
   * @param {*} params 
   * @returns 
   */
  static async getFullLocalizationAsync(params) {
    let response = null

    try {
      silentErrors = true
      const client = await getClient('WebAppModeler')
      response = await client.query({
        query: gql`${getFullLocalization}`,
        variables: params
      })
    } catch (e) {
      throw new Error(e.message)
    } finally {
      silentErrors = false
    }

    return response
  }

  static async getApplicationVersionAsync(appName) {
    try {
      const client = await getClient('WebAppModeler', true)
      const response = await client.query({
        query: gql`${appVersion}`,
        variables: { appName }
      })
      return response
    } catch (e) {
      console.error(e.message)
    }
  }
}
